import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";
import PropTypes from "prop-types";

// All MUI components
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

// All icons
import AddIcon from "@mui/icons-material/Add";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CachedIcon from "@mui/icons-material/Cached";
import HistoryIcon from "@mui/icons-material/History";
import InfoCircleFilled from "@ant-design/icons/InfoCircleFilled";
// Others
import { ButtonUI } from "../../../Common";
import { convertTimezone, isEqual, showLastRunText } from "../../../../util/common";
import DataPipelineService from "../../../../service/dataPipelineService";
import { hideToast, showToast } from "../../../../store/actions";
import { navScheduledJobs } from "../../../kmk/AppData";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import MultiSelectDropDown from "../../../Common/MultiSelectDropDown";
import MultiSelect from "../../../../util/multiselect";
import KMKTable from "../../../Common/Table/KMKTable";
import PipelineHistory from "../JobHistory/PipelineHistory";

const useStyles = makeStyles()({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
  },
  tableButton: {
    textTransform: "inherit !important",
    padding: "4px 12px",
    marginLeft: "10px",
  },
  IconHolder: {
    borderRadius: "50px",
    margin: "0",
    padding: "5px 5px",
    fontSize: "19px",
  },
});

const handleLastRunText = (status, statusText, lastRunTimeInMS, formattedDate) => {
  if (status === "running") return statusText;
  else if (["success", "error", "failed", "terminating", "terminated", "warning"].includes(status))
    return showLastRunText(status, statusText, lastRunTimeInMS, formattedDate);
  else return "-";
};

const PipelineList = (props) => {
  const { dataPipelineList, setDataPipelineList, fetchAllData, selectedPipelines, setJobList, handleLoading } = props;
  const dispatch = useDispatch();

  const { workspace } = useSelector((state) => state.workspace);

  const [historyPopup, setHistoryPopup] = useState({
    open: false,
    DataPipeline_id: null,
    PipelineName: "",
  });

  const { accessRole, IsDataVaultEnabled } = workspace;

  const { Workspace_id } = useParams();

  const { classes } = useStyles();
  const navigate = useNavigate();
  const allowAutoUpdate = useRef(true);

  const $filteredDataPipelineList = useMemo(
    () =>
      selectedPipelines?.length
        ? dataPipelineList.filter(({ DataPipeline_id }) => selectedPipelines.includes(DataPipeline_id))
        : dataPipelineList,
    [dataPipelineList, selectedPipelines]
  );

  const runningPipelineIds = useMemo(
    () =>
      $filteredDataPipelineList
        .filter((pipeline) => ["running", "terminating"]?.includes(pipeline.status))
        .map((pipeline) => pipeline.DataPipeline_id),
    [$filteredDataPipelineList]
  );

  const handleRunPipeline = useCallback(
    async (pipeline) => {
      if (!pipeline?.EntryJob_id) return;
      allowAutoUpdate.current = false;
      handleLoading(true);
      const datapipeline = new DataPipelineService();
      const { success, data, message } = await datapipeline.runDataPipeline(Workspace_id, pipeline?.DataPipeline_id);

      if (success) {
        const job = data?.job || {};
        delete data?.job;

        dispatch(showToast({ message, type: "success" }));

        setDataPipelineList((prev) =>
          prev?.map((prevPipeline) => {
            if (parseInt(prevPipeline?.DataPipeline_id) === parseInt(pipeline?.DataPipeline_id))
              return { ...prevPipeline, ...data };
            return prevPipeline;
          })
        );

        setJobList((prev) =>
          prev?.map((prevJob) => {
            if (parseInt(prevJob?.Job_id) === parseInt(pipeline?.EntryJob_id))
              return {
                ...prevJob,
                ...job,
                status: data?.status,
                statusText: data?.statusText,
                averageRunTimeInMS: data?.averageRunTimeInMS,
                StartTime: data?.StartTime,
              };
            return prevJob;
          })
        );
      }

      allowAutoUpdate.current = true;
      handleLoading(false);
    },
    [Workspace_id, dispatch, handleLoading, setDataPipelineList, setJobList]
  );

  const gotoDataPipeline = useCallback(
    async (pipeline_id = "") =>
      navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/data-pipeline/${pipeline_id || ""}`),
    [Workspace_id, navigate]
  );

  const addNewPipeline = useCallback(
    () => navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/data-pipeline/add`),
    [Workspace_id, navigate]
  );

  const handlePipelineFilter = useCallback(
    async (selectionList) => {
      const orignalList = dataPipelineList?.map(({ DataPipeline_id }) => DataPipeline_id) || [];
      const selectedAll = isEqual(selectionList?.slice().sort(), orignalList?.slice().sort());

      await fetchAllData(selectedAll ? [] : selectionList);
    },
    [dataPipelineList, fetchAllData]
  );

  const fetchStatus = useCallback(
    async (DataPipeline_id) => {
      const pipeline = new DataPipelineService();
      const { success, data } = await pipeline.getDataPipelineStatus(Workspace_id, DataPipeline_id);
      return { success, data };
    },
    [Workspace_id]
  );

  const getPipelineStatus = useCallback(
    async (DataPipeline_id) => {
      allowAutoUpdate.current = false;
      dispatch(hideToast());
      handleLoading(true);

      const { success, data } = await fetchStatus(DataPipeline_id);

      if (success)
        setDataPipelineList((prev) =>
          prev?.map((prevPipeline) => {
            if (parseInt(prevPipeline?.DataPipeline_id) === parseInt(DataPipeline_id))
              return { ...prevPipeline, ...data };
            return prevPipeline;
          })
        );

      handleLoading(false);
      allowAutoUpdate.current = true;
    },
    [dispatch, fetchStatus, handleLoading, setDataPipelineList]
  );

  const columns = useMemo(
    () => [
      {
        id: "Name",
        name: "NAME",
        style: { minWidth: 220, wordBreak: "break-word" },
        align: "left",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ m: 0, p: 0, fontSize: "0.875rem" }}>{record.Name}</Typography>
            {record.IsDefault === 1 && (
              <Tooltip placement="right" title="Default Pipeline" arrow>
                <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        id: "EntryJob",
        name: "ENTRY POINT",
        style: { minWidth: 220, wordBreak: "break-word" },
        align: "left",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "Schedule",
        name: `SCHEDULE`,
        style: { minWidth: 220, wordBreak: "break-word" },
        align: "left",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          if (record.Enabled) {
            if (record.nextRun)
              return `${record.Schedule ? record.Schedule : ""}\n\rNext Job run time: ${convertTimezone(
                record.nextRun
              )}`;
            else return "";
          }

          return record?.EntryJob_id ? "Disabled or Not scheduled" : "-";
        },
      },
      {
        id: "status",
        name: "STATUS",
        style: { minWidth: 150, wordBreak: "break-word" },
        align: "left",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <>
            {!val && "-"}
            {["ready", "running", "draft"].includes(val) && record?.statusText}
            {["terminating"].includes(val) && "Running"}
            {val && !["ready", "running", "draft", "terminating"].includes(val) && "Ready"}
          </>
        ),
      },
      {
        id: "statusText",
        name: `LAST RUN`,
        style: { minWidth: 150, wordBreak: "break-word" },
        align: "left",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <>
            {["error", "failed", "warning"].includes(record.status) && (
              <Tooltip title={record.statusText}>
                <IconButton
                  size="small"
                  color={["error", "failed"].includes(record.status) ? "error" : "warning"}
                  sx={{ mr: "5px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoCircleFilled />
                </IconButton>
              </Tooltip>
            )}
            {handleLastRunText(
              record.status,
              record.statusText,
              record.lastRunTimeInMS,
              convertTimezone(record.StartTime || null)
            )}
          </>
        ),
      },
      {
        id: "Action",
        name: "ACTION",
        style: { minWidth: 120, wordBreak: "break-word" },
        align: "right",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <div style={{ display: "flex", justifyContent: "right", margin: "0 auto !important" }}>
            <Box sx={{ minHeight: 30, minWidth: 30 }}>
              <Tooltip title="History">
                <span
                  style={
                    [3].includes(accessRole)
                      ? {
                          paddingRight: "25px",
                        }
                      : {}
                  }
                >
                  <IconButton
                    className={classes.IconHolder}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!(record.status && ["draft"].includes(record.status)))
                        setHistoryPopup({
                          open: true,
                          DataPipeline_id: record.DataPipeline_id,
                          PipelineName: record.Name,
                        });
                    }}
                    sx={
                      record.status && ["draft"].includes(record.status)
                        ? { color: "rgba(0, 0, 0, 0.26)" }
                        : { color: "#0288D1" }
                    }
                  >
                    <HistoryIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
              {[1, 2].includes(accessRole) && record.EntryJob_id ? (
                <>
                  <Tooltip title="Refresh" placement="top-start">
                    <span>
                      <IconButton
                        variant="contained"
                        color="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!(record.status && ["draft"].includes(record.status)))
                            getPipelineStatus(record.DataPipeline_id);
                        }}
                        sx={{ color: "#1B5E20" }}
                        className={classes.IconHolder}
                      >
                        <CachedIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Run Now">
                    <span>
                      <IconButton
                        variant="contained"
                        color="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRunPipeline(record);
                        }}
                        disabled={["running", "terminating"]?.includes(record.status)}
                        className={classes.IconHolder}
                      >
                        <PlayCircleOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : null}
            </Box>
          </div>
        ),
      },
    ],
    [accessRole, classes.IconHolder, getPipelineStatus, handleRunPipeline]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!allowAutoUpdate.current || runningPipelineIds.length === 0) return;
      const runningPipelines = await Promise.all(
        runningPipelineIds?.map((DataPipeline_id) => fetchStatus(DataPipeline_id))
      );

      setDataPipelineList((prev) =>
        prev?.map((prevPipeline) => {
          if (runningPipelineIds?.includes(parseInt(prevPipeline?.DataPipeline_id))) {
            const currPipeline =
              runningPipelines?.find(
                ({ success, data }) =>
                  success && parseInt(prevPipeline?.DataPipeline_id) === parseInt(data?.DataPipeline_id)
              ) || {};

            if (currPipeline) return { ...prevPipeline, ...currPipeline?.data };
          }
          return prevPipeline;
        })
      );
    };

    const interval = setInterval(fetchData, 20000); // Update every 20 seconds

    return () => clearInterval(interval);
  }, [Workspace_id, setJobList, runningPipelineIds, fetchStatus, setDataPipelineList]);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid sx={{ mb: 1, display: "flex", alignItems: "center" }} size={12}>
          <Typography className={classes.HeaderTitle} component="div">
            Data Pipelines
          </Typography>
          {IsDataVaultEnabled && [1, 2].includes(accessRole) ? (
            <ButtonUI
              variant="contained"
              color="info"
              className={classes.tableButton}
              sx={{ marginLeft: "0px !important" }}
              onClick={() => addNewPipeline()}
              endIcon={<AddIcon />}
            >
              Add Pipeline
            </ButtonUI>
          ) : null}
          <HelpContextIcon screen="Data Pipelines" />
          <Box sx={{ marginLeft: "auto" }} p={0} m={0}>
            <MultiSelectDropDown
              dataid="PipelineFilter"
              variant="standard"
              placeHolder={
                <MultiSelect title="Pipeline" dataLength={selectedPipelines?.length || dataPipelineList?.length} />
              }
              options={dataPipelineList}
              selected={
                selectedPipelines?.length
                  ? selectedPipelines
                  : dataPipelineList?.map(({ DataPipeline_id }) => DataPipeline_id)
              }
              basedOn="DataPipeline_id"
              isSelectAll={true}
              onSelectChange={(selectionList) => handlePipelineFilter(selectionList)}
              showIconFor={[
                ...(dataPipelineList
                  ?.filter((pipeline) => pipeline.IsDefault === 1)
                  .map((pipeline) => pipeline.DataPipeline_id) || []),
              ]}
              icon={
                <Tooltip placement="right" title="Default Pipeline" arrow>
                  <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                </Tooltip>
              }
            />
          </Box>
        </Grid>
        <Grid className={classes.BigTileContainer} size={12}>
          <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
            <KMKTable
              columns={columns}
              containerHeight={450}
              noDataText="No data available"
              data={$filteredDataPipelineList}
              sortingProperties={{}}
              paginationProperties={{}}
              boldHeader
              tableProps={{ size: "medium", stickyHeader: true }}
              dataIdKey="App_id"
              onRowClick={(record) => {
                if ([1, 2].includes(accessRole)) gotoDataPipeline(record.DataPipeline_id);
              }}
            />
          </Paper>
        </Grid>
      </Grid>
      {historyPopup.open && (
        <PipelineHistory
          DataPipeline_id={historyPopup.DataPipeline_id}
          PipelineName={historyPopup.PipelineName}
          handleCancel={() => setHistoryPopup((prev) => ({ ...prev, open: false }))}
        />
      )}
    </>
  );
};

PipelineList.propTypes = {
  dataPipelineList: PropTypes.array.isRequired,
  fetchAllData: PropTypes.func.isRequired,
  setDataPipelineList: PropTypes.func.isRequired,
  selectedPipelines: PropTypes.func.isRequired,
  setJobList: PropTypes.func.isRequired,
  handleLoading: PropTypes.func.isRequired,
};

export default memo(PipelineList);
